'use client';

import type { UpsertSurfaceProfileDto } from '@terra/shared-contracts-external-network';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { Field } from '../../../components/field';
import { Select, createSelectListCollection } from '../../../components/select';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import {
  BUSINESS_PROFILE_COMPANY_SIZES,
  isBusinessProfileCompanySize,
  type BusinessProfileCompanySize,
} from './business-profile.model';

export type BusinessProfileCompanySizeDialogProps = {
  open: boolean;
  initialValue: BusinessProfileCompanySize | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: (dto: UpsertSurfaceProfileDto) => Promise<boolean>;
};

export function BusinessProfileCompanySizeDialog({
  open,
  initialValue,
  loading,
  onClose,
  onSubmit,
}: BusinessProfileCompanySizeDialogProps) {
  const t = useTranslations('designSystem.networkSettings.businessProfile');
  const [draft, setDraft] = useDraftOnOpen({ open, value: initialValue });
  const collection = useMemo(
    () =>
      createSelectListCollection({
        items: BUSINESS_PROFILE_COMPANY_SIZES.map((value) => ({
          label: t(`companySize.${value}`),
          value,
        })),
      }),
    [t],
  );

  const handleSubmit = async () => {
    const saved = await onSubmit({ companySize: draft });

    if (saved) {
      onClose();
    }
  };

  return (
    <SettingsEditDialog
      open={open}
      title={t('dialogs.editCompanySize')}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      onSubmit={() => void handleSubmit()}
      loading={loading}
    >
      <Field.Root>
        <Field.Label>{t('metadata.companySize')}</Field.Label>
        <Select.Root
          positioning={{ sameWidth: true }}
          collection={collection}
          value={draft === null ? [] : [draft]}
          onValueChange={(event) => {
            const selectedValue = event.value[0];

            setDraft(
              selectedValue && isBusinessProfileCompanySize(selectedValue)
                ? selectedValue
                : null,
            );
          }}
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder={t('emptyValue')} />
              <ArrowUpDownIcon size={14} />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              <Select.ItemGroup>
                {collection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Tick01Icon size={14} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.ItemGroup>
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Field.Root>
    </SettingsEditDialog>
  );
}
